import React from 'react'
import PropTypes from 'prop-types'
import MarkerIcon from '../../../svg/Marker.svg'
import ViewLocation from '../../../svg/ViewLocation.svg'
import { Wrapper } from '@googlemaps/react-wrapper'
import { useRef, useEffect, useState } from 'react'
import { trackMapInteraction } from '../../../utils/sendGAAnalyticsEvent'
import useAuth from '../../../hooks/useAuth'
import { createRoot } from 'react-dom/client'

function Marker({ map, position, children }) {
    const rootRef = useRef()
    const reactRootRef = useRef()
    const markerRef = useRef()

    useEffect(() => {
        if (!rootRef.current) {
            const container = document.createElement('div')
            rootRef.current = container
            reactRootRef.current = createRoot(container)

            markerRef.current = new window.google.maps.marker.AdvancedMarkerView(
                {
                    position,
                    content: container,
                    map,
                },
            )
        }

        return () => {
            if (reactRootRef.current) {
                reactRootRef.current.unmount()
                reactRootRef.current = null
            }
            rootRef.current = null
            markerRef.current = null
        }
        // Depend on the primitive lat/lng instead of the `position` object
        // reference, which is recreated on every parent render even when the
        // coordinates haven't changed. Otherwise the root gets torn down and
        // left unmounted while a stale ref lingers, and the effect below can
        // try to render into it, throwing "Cannot update an unmounted root".
    }, [map, position.lat, position.lng])

    useEffect(() => {
        if (children && reactRootRef.current) {
            reactRootRef.current.render(children)
        }
    }, [children])

    return null
}

Marker.propTypes = {
    map: PropTypes.PropTypes.any,
    position: PropTypes.PropTypes.any,
    children: PropTypes.PropTypes.any,
}
function MyMap({ location }) {
    const [map, setMap] = useState()
    const ref = useRef()
    const { user } = useAuth()

    const mapOptions = {
        center: { lat: location.lat, lng: location.lng }, // Position map
        zoom: 17,
        disableDefaultUI: true,
        mapId: process.env.MIX_MAPS_GOOGLE_ID,
    }

    useEffect(() => {
        if (!ref.current) return
        const mapInstance = new window.google.maps.Map(ref.current, mapOptions)
        setMap(mapInstance)

        const handleUserInteraction = () => {
            trackMapInteraction(user?.external_contact_id)
        }
        mapInstance.addListener('click', handleUserInteraction)
        mapInstance.addListener('dragstart', handleUserInteraction)

        return () => {
            window.google.maps.event.clearListeners(mapInstance, 'click')
            window.google.maps.event.clearListeners(mapInstance, 'dragstart')
        }
    }, [])

    return (
        <>
            <div ref={ref} id="map" style={{ height: '100%', width: '100%' }} />
            {map && (
                <Marker
                    map={map}
                    position={{ lat: location.lat, lng: location.lng }} // Position marker
                >
                    <img
                        src={MarkerIcon}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                </Marker>
            )}
        </>
    )
}
MyMap.propTypes = {
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
}

function MapLocation({ location }) {
    return (
        <Wrapper
            apiKey={process.env.MIX_MAPS_APP_KEY}
            version="beta"
            libraries={['marker']}
        >
            <div className="rounded-2xl border overflow-hidden border-grayLines flex-grow max-h-40 w-full max-w-full min-w-full h-40 relative">
                <a
                    target="_blank"
                    href={`https://maps.google.com/?q=${location?.lat},${location?.lng}`}
                    className="absolute w-auto h-auto right-2 top-2 z-100 flex box-border items-center justify-center p-1 border border-grayLines bg-white rounded-md"
                    rel="noreferrer"
                >
                    <img src={ViewLocation} className="w-7.5 h-7.5" />
                </a>
                <MyMap location={location} />
            </div>
        </Wrapper>
    )
}

MapLocation.propTypes = {
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
}

export default React.memo(MapLocation)
